import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/ui/multi-select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

type QuestionGroup = Tables<"question_groups">;

const questionGroupSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  questions_per_test: z.number().min(1, "Must be at least 1"),
  must_have_questions: z.number().min(0, "Must be at least 0"),
  job_position_ids: z.array(z.string()).optional().default([]),
  sort_order: z.number().min(0, "Sort order must be at least 0"),
});

type QuestionGroupForm = z.infer<typeof questionGroupSchema>;

interface QuestionGroupDialogProps {
  open: boolean;
  onClose: () => void;
  editingGroup: QuestionGroup | null;
  onSuccess: () => void;
}

export function QuestionGroupDialog({
  open,
  onClose,
  editingGroup,
  onSuccess,
}: QuestionGroupDialogProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch job positions for the multi-select
  const { data: jobPositions = [] } = useQuery({
    queryKey: ["job-positions", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_positions")
        .select("id, role_name, job_type")
        .eq("is_active", true)
        .order("role_name");
      if (error) throw error;
      return data || [];
    },
  });

  const jobPositionOptions = jobPositions.map((position) => ({
    value: position.id,
    label: position.role_name,
  }));

  const form = useForm<QuestionGroupForm>({
    resolver: zodResolver(questionGroupSchema),
    defaultValues: {
      name: "",
      description: "",
      questions_per_test: 10,
      must_have_questions: 0,
      job_position_ids: [],
      sort_order: 0,
    },
  });

  useEffect(() => {
    if (editingGroup) {
      form.reset({
        name: editingGroup.name,
        description: editingGroup.description || "",
        questions_per_test: editingGroup.questions_per_test,
        must_have_questions: editingGroup.must_have_questions,
        job_position_ids: editingGroup.job_position_ids || [],
        sort_order: editingGroup.sort_order || 0,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        questions_per_test: 10,
        must_have_questions: 0,
        job_position_ids: [],
        sort_order: 0,
      });
    }
  }, [editingGroup, form]);

  const onSubmit = async (data: QuestionGroupForm) => {
    setLoading(true);

    try {
      const groupData = {
        name: data.name,
        description: data.description || null,
        questions_per_test: data.questions_per_test,
        must_have_questions: data.must_have_questions,
        job_position_ids: data.job_position_ids,
        sort_order: data.sort_order,
      };

      if (editingGroup) {
        const { error } = await supabase
          .from("question_groups")
          .update(groupData)
          .eq("id", editingGroup.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Question group updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("question_groups")
          .insert(groupData);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Question group created successfully",
        });
      }

      // Clear form after successful submission
      form.reset();
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save question group",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingGroup ? "Edit Question Group" : "Create Question Group"}
          </DialogTitle>
          <DialogDescription>
            Question groups help organize questions and define how many
            questions to include in tests.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Programming Basics" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what this group covers..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="job_position_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Positions</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={jobPositionOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select job positions"
                    />
                  </FormControl>
                  <FormDescription>
                    Select which job positions this question group applies to.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="questions_per_test"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Questions per Test</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    How many questions from this group to include in each test
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="must_have_questions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Must-have Questions</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    How many questions marked as "must-have" to always include
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sort_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Determines the sequence of this group in assessments
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : editingGroup ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
